import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  doc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import { calculatePoints, cleanPhone, rewardForPoints } from "../../utils/customerEntryPoints";
import { amountsSimilar, formatCustomerEntryActorLabel, resolveCustomerEntryEnteredBy } from "../../utils/customerEntryProfile";
import { writeAuditLog } from "../../utils/auditLog";
import { CE_DRAFT_KEY } from "./customerEntryDraft";
import { useCustomerEntries } from "./customerEntriesStore";

const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Finds a non-voided entry that already uses this invoice (exact or case-insensitive / comma-separated legacy).
 * `rows` is the live customer_entries list from {@link useCustomerEntries}.
 */
async function findExistingEntryForInvoice(invoiceRaw, rows) {
  const inv = String(invoiceRaw ?? "").trim();
  if (!inv) return null;
  const invLower = inv.toLowerCase();

  const q1 = query(collection(db, "customer_entries"), where("invoiceNumbers", "array-contains", inv), limit(25));
  const s1 = await getDocs(q1);
  for (const d of s1.docs) {
    if ((d.data() || {}).voided === true) continue;
    return d;
  }

  const q2 = query(collection(db, "customer_entries"), where("invoiceNo", "==", inv), limit(25));
  const s2 = await getDocs(q2);
  for (const d of s2.docs) {
    if ((d.data() || {}).voided === true) continue;
    return d;
  }

  const asDocSnap = (row) => ({ id: row.id, data: () => row.data });
  for (const row of rows) {
    if (row.voided) continue;
    const data = row.data;
    if (Array.isArray(data.invoiceNumbers)) {
      for (const x of data.invoiceNumbers) {
        if (String(x).trim().toLowerCase() === invLower) return asDocSnap(row);
      }
    }
    const raw = String(data.invoiceNo ?? "");
    for (const part of raw.split(",")) {
      if (part.trim().toLowerCase() === invLower) return asDocSnap(row);
    }
  }
  return null;
}

function formatDuplicateEntryDetails(docSnap) {
  const data = docSnap.data() || {};
  const created = data.createdAt?.toDate?.();
  const invDisplay =
    Array.isArray(data.invoiceNumbers) && data.invoiceNumbers.length
      ? data.invoiceNumbers.join(", ")
      : String(data.invoiceNo ?? "—");
  const amt = Number(data.amount) || 0;
  const pts =
    data.points != null && data.points !== ""
      ? Number(data.points)
      : calculatePoints(amt);
  return {
    docId: docSnap.id,
    customerName: String(data.customerName ?? "—").trim() || "—",
    phone: String(data.phone ?? "—").trim() || "—",
    amount: amt,
    points: pts,
    invoiceDisplay: invDisplay,
    enteredBy: formatCustomerEntryActorLabel(data.enteredBy ?? "—") || "—",
    createdAtLabel: created
      ? created.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
      : "—",
  };
}

function aggregateCustomers(rows) {
  const map = new Map();
  for (const row of rows) {
    if (row.voided) continue;
    const data = row.data;
    const name = String(data.customerName ?? "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const amount = Number(data.amount) || 0;
    const phone = String(data.phone ?? "").trim();
    if (map.has(key)) {
      const cur = map.get(key);
      cur.totalAmount += amount;
      cur.count += 1;
    } else {
      map.set(key, {
        name,
        phone,
        totalAmount: amount,
        count: 1,
      });
    }
  }
  return Array.from(map.values());
}

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20";

export default function CustomerEntryFormTab({ onSaved, variant }) {
  const isModal = variant === "modal";
  const auth = getAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pairs, setPairs] = useState([{ invoice: "", amount: "" }]);
  const { rows: entryRows, loading: entriesLoading } = useCustomerEntries();
  const allCustomers = useMemo(() => aggregateCustomers(entryRows), [entryRows]);
  const [selectedName, setSelectedName] = useState(null);
  const [customerTotalAmount, setCustomerTotalAmount] = useState(0);
  const [customerTotalPoints, setCustomerTotalPoints] = useState(0);
  const [giftSettings, setGiftSettings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const draftTimer = useRef(null);
  const draftLoaded = useRef(false);
  const invoiceRefs = useRef([]);
  const amountRefs = useRef([]);
  const pendingInvoiceFocusIdx = useRef(null);

  useEffect(() => {
    const idx = pendingInvoiceFocusIdx.current;
    if (idx == null) return;
    pendingInvoiceFocusIdx.current = null;
    requestAnimationFrame(() => {
      invoiceRefs.current[idx]?.focus();
      invoiceRefs.current[idx]?.select?.();
    });
  }, [pairs.length]);

  const focusAmountAt = (idx) => {
    requestAnimationFrame(() => {
      amountRefs.current[idx]?.focus();
      amountRefs.current[idx]?.select?.();
    });
  };

  const onInvoiceKeyDown = (e, idx) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    focusAmountAt(idx);
  };

  const onAmountKeyDown = (e, idx) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const nextIdx = idx + 1;
    if (nextIdx < pairs.length) {
      requestAnimationFrame(() => {
        invoiceRefs.current[nextIdx]?.focus();
        invoiceRefs.current[nextIdx]?.select?.();
      });
      return;
    }
    pendingInvoiceFocusIdx.current = nextIdx;
    setPairs((p) => [...p, { invoice: "", amount: "" }]);
  };

  useEffect(() => {
    const qy = query(collection(db, "customer_reward_gifts"), orderBy("pointsRequired", "desc"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs
          .map((d) => d.data() || {})
          .filter((r) => r.active !== false)
          .map((r) => ({
            giftName: String(r.giftName || "").trim(),
            pointsRequired: Number(r.pointsRequired) || 0,
          }))
          .filter((r) => r.giftName && r.pointsRequired > 0)
          .sort((a, b) => b.pointsRequired - a.pointsRequired);
        setGiftSettings(list);
      },
      () => {
        setGiftSettings([]);
      }
    );
    return () => unsub();
  }, []);

  // Restore draft once (modal or page)
  useEffect(() => {
    if (draftLoaded.current) return;
    draftLoaded.current = true;
    try {
      const raw = localStorage.getItem(CE_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      if (parsed.savedAt && Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(CE_DRAFT_KEY);
        return;
      }
      if (parsed.name != null) setName(String(parsed.name));
      if (parsed.phone != null) setPhone(String(parsed.phone));
      if (Array.isArray(parsed.pairs) && parsed.pairs.length) {
        setPairs(parsed.pairs.map((p) => ({ invoice: String(p.invoice ?? ""), amount: String(p.amount ?? "") })));
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Debounced draft autosave
  useEffect(() => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      try {
        const payload = {
          name,
          phone,
          pairs,
          savedAt: Date.now(),
        };
        const hasContent =
          name.trim() ||
          phone.trim() ||
          pairs.some((p) => p.invoice.trim() || String(p.amount).trim());
        if (!hasContent) {
          localStorage.removeItem(CE_DRAFT_KEY);
          return;
        }
        localStorage.setItem(CE_DRAFT_KEY, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
    }, 450);
    return () => {
      if (draftTimer.current) clearTimeout(draftTimer.current);
    };
  }, [name, phone, pairs]);

  const totalAmount = useMemo(() => {
    let t = 0;
    for (const p of pairs) {
      t += parseFloat(String(p.amount).replace(/,/g, "")) || 0;
    }
    return t;
  }, [pairs]);

  const totalPoints = useMemo(() => calculatePoints(totalAmount), [totalAmount]);
  const getConfiguredReward = useCallback(
    (pts) => {
      const p = Number(pts) || 0;
      if (giftSettings.length > 0) {
        const hit = giftSettings.find((g) => p >= g.pointsRequired);
        if (hit) return { item: hit.giftName, points: hit.pointsRequired };
        return null;
      }
      return rewardForPoints(p);
    },
    [giftSettings]
  );
  const currentReward = useMemo(() => getConfiguredReward(totalPoints), [getConfiguredReward, totalPoints]);
  const achievedReward = useMemo(
    () => getConfiguredReward(customerTotalPoints),
    [getConfiguredReward, customerTotalPoints]
  );

  const calcCustomerTotalPoints = useCallback(
    (customerName) => {
      if (!customerName.trim()) return 0;
      let pts = 0;
      for (const row of entryRows) {
        if (row.voided || row.data.customerName !== customerName) continue;
        const amount = Number(row.data.amount) || 0;
        const stored = row.data.points;
        if (stored != null && stored !== "") {
          pts += Number(stored) || 0;
        } else {
          pts += calculatePoints(amount);
        }
      }
      return pts;
    },
    [entryRows]
  );

  useEffect(() => {
    const n = name.trim();
    if (!n) {
      setSelectedName(null);
      setCustomerTotalAmount(0);
      setCustomerTotalPoints(0);
      return;
    }
    const match = allCustomers.find((c) => c.name.toLowerCase() === n.toLowerCase());
    if (match) {
      setSelectedName(match.name);
      setCustomerTotalAmount(match.totalAmount || 0);
      setCustomerTotalPoints(calcCustomerTotalPoints(match.name));
      setPhone((prev) => {
        if (!prev.trim() && match.phone) return match.phone;
        return prev;
      });
    } else {
      setSelectedName(null);
      setCustomerTotalPoints(0);
      setCustomerTotalAmount(0);
    }
  }, [name, allCustomers, calcCustomerTotalPoints]);

  const resolveEnteredBy = async () => resolveCustomerEntryEnteredBy(auth, db);

  const sendSmsAfterSave = async (docId, clean, customerName, invoiceDisplay, amt, pts) => {
    const message = `Dear ${customerName},\n\nInvoice: ${invoiceDisplay}\nAmount: ₹${Math.round(amt)}\nPoints: ${Math.round(pts)}\n\nThank you for your purchase!`;
    const uri = `sms:${clean}?body=${encodeURIComponent(message)}`;
    try {
      window.location.href = uri;
      await updateDoc(doc(db, "customer_entries", docId), {
        messageSent: true,
        messageSentAt: serverTimestamp(),
        messageText: message,
        messageAutoOpened: true,
        messageStatus: "opened",
      });
    } catch (e) {
      console.warn(e);
    }
  };

  const save = async () => {
    const customerName = name.trim();
    const phoneTrim = phone.trim();
    if (!customerName) {
      toast.error("Enter customer name");
      return;
    }
    if (!phoneTrim) {
      toast.error("Enter phone number");
      return;
    }

    const invoiceAmountPairs = [];
    let sum = 0;
    const invoiceNumbers = [];
    for (const p of pairs) {
      const inv = p.invoice.trim();
      const amt = parseFloat(String(p.amount).replace(/,/g, "")) || 0;
      if (inv && amt > 0) {
        invoiceAmountPairs.push({ invoice: inv, amount: amt });
        sum += amt;
        invoiceNumbers.push(inv);
      }
    }
    if (invoiceAmountPairs.length === 0) {
      toast.error("Please enter at least one invoice with amount");
      return;
    }

    const firstInv = pairs[0]?.invoice?.trim();
    const firstAmt = parseFloat(String(pairs[0]?.amount).replace(/,/g, "")) || 0;
    if (!firstInv) {
      toast.error("Enter at least one invoice number");
      return;
    }
    if (!firstAmt || firstAmt <= 0) {
      toast.error("Enter valid amount for first row");
      return;
    }

    for (let i = 1; i < pairs.length; i++) {
      const amtStr = pairs[i].amount.trim();
      if (amtStr && (!parseFloat(amtStr) || parseFloat(amtStr) <= 0)) {
        toast.error("Invalid amount");
        return;
      }
    }

    const totalPts = calculatePoints(sum);
    const invoiceDisplay = invoiceNumbers.length === 1 ? invoiceNumbers[0] : invoiceNumbers.join(", ");

    if (entriesLoading) {
      toast.error("Customer list is still loading. Try again in a moment.");
      return;
    }

    const phoneClean = cleanPhone(phoneTrim);
    if (phoneClean) {
      try {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        for (const row of entryRows) {
          if (row.voided) continue;
          const data = row.data;
          if (cleanPhone(String(data.phone ?? "")) !== phoneClean) continue;
          const cr = data.createdAt?.toDate?.();
          if (!cr || cr < start || cr >= end) continue;
          if (amountsSimilar(Number(data.amount) || 0, sum)) {
            const otherName = String(data.customerName ?? "").trim() || "—";
            const ok = window.confirm(
              `Possible duplicate: same phone today with a similar amount (existing ₹${Number(data.amount).toFixed(0)} for "${otherName}").\n\nSave anyway?`
            );
            if (!ok) return;
            break;
          }
        }
      } catch (e) {
        console.warn(e);
      }
    }

    for (const inv of invoiceNumbers) {
      try {
        const existing = await findExistingEntryForInvoice(inv, entryRows);
        if (existing) {
          setDuplicateModal({ invoice: inv, docSnap: existing });
          toast.error("This invoice number is already saved.");
          return;
        }
      } catch (e) {
        console.error(e);
        toast.error("Could not verify invoice numbers. Try again.");
        return;
      }
    }

    setSaving(true);
    try {
      if (phoneTrim && phoneTrim !== "-") {
        const cP = cleanPhone(phoneTrim);
        if (cP) {
          const conflict = allCustomers.filter((c) => {
            const p = String(c.phone ?? "").trim();
            if (!p || p === "-") return false;
            return cleanPhone(p) === cP && c.name.toLowerCase() !== customerName.toLowerCase();
          });
          if (conflict.length) {
            toast.error("Duplicate phone number. This number is already registered under another name.");
            setSaving(false);
            return;
          }
        }
      }

      const user = auth.currentUser;
      const enteredBy = await resolveEnteredBy();

      const docRef = await addDoc(collection(db, "customer_entries"), {
        customerName,
        phone: phoneTrim,
        invoiceNo: invoiceDisplay,
        invoiceNumbers,
        invoiceAmountPairs,
        amount: sum,
        points: totalPts,
        createdAt: serverTimestamp(),
        createdByUid: user?.uid ?? null,
        createdByEmail: user?.email ?? null,
        enteredBy,
      });

      try {
        const role = (() => {
          try {
            return JSON.parse(localStorage.getItem("user") || "{}")?.role;
          } catch {
            return "unknown";
          }
        })();
        await writeAuditLog({
          entity: "customer_entry",
          entityId: docRef.id,
          action: "create",
          extra: { customerName, amount: sum, phone: phoneTrim },
          uid: user?.uid ?? null,
          email: user?.email ?? null,
          displayName: user?.displayName ?? null,
          role: role || "unknown",
        });
      } catch (e) {
        console.warn("audit log", e);
      }

      try {
        localStorage.removeItem(CE_DRAFT_KEY);
      } catch {
        /* ignore */
      }

      if (phoneTrim && phoneTrim !== "-") {
        const c = cleanPhone(phoneTrim);
        if (c) await sendSmsAfterSave(docRef.id, c, customerName, invoiceDisplay, sum, totalPts);
      }

      toast.success("Saved successfully");
      setName("");
      setPhone("");
      setPairs([{ invoice: "", amount: "" }]);
      onSaved?.();
    } catch (e) {
      console.error(e);
      toast.error(`Save failed: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const nameOptions = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (!q) return [];
    return allCustomers
      .filter((c) => {
        const nm = (c.name || "").toLowerCase();
        const ph = String(c.phone || "").replace(/\s/g, "");
        const qn = q.replace(/\s/g, "");
        return nm.includes(q) || ph.includes(qn);
      })
      .map((c) => c.name)
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, 10);
  }, [name, allCustomers]);

  const phoneOptions = useMemo(() => {
    const q = phone.trim();
    if (!q) return [];
    return allCustomers
      .filter((c) => {
        const ph = String(c.phone || "");
        return ph.includes(q);
      })
      .map((c) => c.phone)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .slice(0, 10);
  }, [phone, allCustomers]);

  const formBody = (
    <div className={`space-y-4 ${isModal ? "" : "p-5"}`}>
          <div>
            <label className="text-xs font-medium text-gray-600">Customer name *</label>
            <input
              list="ce-name-options"
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Start typing to search existing…"
            />
            <datalist id="ce-name-options">
              {nameOptions.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>

          {selectedName && customerTotalPoints > 0 && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/80 px-4 py-3 text-sm">
              <p className="text-gray-600">
                Previous total: <span className="font-medium text-gray-900">₹{customerTotalAmount.toFixed(2)}</span>
              </p>
              <p className="mt-1 font-semibold text-indigo-700">Loyalty points so far: {customerTotalPoints.toFixed(2)}</p>
              <p className="mt-1 text-indigo-900">
                Achieved so far:{" "}
                <span className="font-semibold">
                  {achievedReward ? `${achievedReward.item} (${achievedReward.points} pts)` : "Below 50 points"}
                </span>
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-600">Phone *</label>
            <input
              list="ce-phone-options"
              type="tel"
              className={inputClass}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile number"
            />
            <datalist id="ce-phone-options">
              {phoneOptions.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">Invoices &amp; amounts</span>
              <button
                type="button"
                onClick={() => setPairs((p) => [...p, { invoice: "", amount: "" }])}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                + Add row
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              Enter on invoice → amount · Enter on amount → next invoice
            </p>
            <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200/90">
              <table className="w-full min-w-[280px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/90 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                    <th className="px-3 py-2">Invoice no.</th>
                    <th className="w-[8.5rem] px-3 py-2">Amount</th>
                    <th className="w-10 px-1 py-2" aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-100 last:border-0">
                      <td className="px-2 py-2 align-top">
                        <input
                          ref={(el) => {
                            invoiceRefs.current[idx] = el;
                          }}
                          className={`${inputClass} mt-0`}
                          placeholder={idx === 0 ? "e.g. INV-1024 *" : `Invoice ${idx + 1}`}
                          value={row.invoice}
                          onChange={(e) => {
                            const next = [...pairs];
                            next[idx] = { ...next[idx], invoice: e.target.value };
                            setPairs(next);
                          }}
                          onKeyDown={(e) => onInvoiceKeyDown(e, idx)}
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          ref={(el) => {
                            amountRefs.current[idx] = el;
                          }}
                          className={`${inputClass} mt-0`}
                          placeholder="Amount (₹)"
                          inputMode="decimal"
                          value={row.amount}
                          onChange={(e) => {
                            const next = [...pairs];
                            next[idx] = { ...next[idx], amount: e.target.value };
                            setPairs(next);
                          }}
                          onKeyDown={(e) => onAmountKeyDown(e, idx)}
                        />
                      </td>
                      <td className="px-1 py-2 align-top">
                        {pairs.length > 1 ? (
                          <button
                            type="button"
                            className="mt-2 rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            aria-label="Remove row"
                            onClick={() => setPairs((p) => p.filter((_, i) => i !== idx))}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        ) : (
                          <span className="inline-block w-9" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalAmount > 0 && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/90 px-4 py-3 text-sm">
              <p className="font-semibold text-emerald-900">Total amount: ₹{totalAmount.toFixed(2)}</p>
              <p className="mt-1 text-emerald-800">
                Points earned this entry: <span className="font-bold">{Math.round(totalPoints)}</span>
                <span className="text-emerald-700/80"> (₹2000 = 1 pt tier rules)</span>
              </p>
              <p className="mt-1 text-emerald-900">
                Reward level:{" "}
                <span className="font-semibold">
                  {currentReward ? `${currentReward.item} (${currentReward.points} pts)` : "Below 50 points"}
                </span>
              </p>
            </div>
          )}


          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-700 disabled:opacity-55"
          >
            {saving ? "Saving…" : "Save customer"}
          </button>
    </div>
  );

  const duplicateDialog =
    duplicateModal &&
    (() => {
      const det = formatDuplicateEntryDetails(duplicateModal.docSnap);
      return (
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="ce-dup-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label="Close"
            onClick={() => setDuplicateModal(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-amber-200 bg-white p-5 shadow-2xl">
            <h3 id="ce-dup-title" className="text-lg font-semibold text-amber-900">
              Invoice already saved
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              The invoice number{" "}
              <span className="font-mono font-semibold text-gray-900">{duplicateModal.invoice}</span> is already on file.
              Your new entry was <strong>not</strong> saved.
            </p>
            <p className="mt-2 text-xs text-gray-500">Existing record details:</p>
            <dl className="mt-3 space-y-2 rounded-xl bg-gray-50 p-3 text-sm text-gray-800">
              <div className="flex justify-between gap-3 border-b border-gray-200/80 pb-2">
                <dt className="text-gray-500">Customer</dt>
                <dd className="text-right font-medium">{det.customerName}</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-gray-200/80 pb-2">
                <dt className="text-gray-500">Phone</dt>
                <dd className="text-right font-mono text-xs">{det.phone}</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-gray-200/80 pb-2">
                <dt className="text-gray-500">Invoice(s)</dt>
                <dd className="max-w-[60%] text-right font-mono text-xs">{det.invoiceDisplay}</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-gray-200/80 pb-2">
                <dt className="text-gray-500">Amount</dt>
                <dd className="text-right font-semibold tabular-nums">₹{det.amount.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-gray-200/80 pb-2">
                <dt className="text-gray-500">Points</dt>
                <dd className="text-right tabular-nums">{Math.round(det.points)}</dd>
              </div>
              <div className="flex justify-between gap-3 border-b border-gray-200/80 pb-2">
                <dt className="text-gray-500">Entered by</dt>
                <dd className="text-right text-xs">{det.enteredBy}</dd>
              </div>
              <div className="flex justify-between gap-3 pt-1">
                <dt className="text-gray-500">Saved at</dt>
                <dd className="text-right text-xs">{det.createdAtLabel}</dd>
              </div>
              <div className="flex justify-between gap-3 border-t border-gray-200/80 pt-2">
                <dt className="text-gray-500">Document ID</dt>
                <dd className="max-w-[55%] break-all text-right font-mono text-[10px] text-gray-600">{det.docId}</dd>
              </div>
            </dl>
            <button
              type="button"
              className="mt-4 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700"
              onClick={() => setDuplicateModal(null)}
            >
              OK
            </button>
          </div>
        </div>
      );
    })();

  if (isModal) {
    return (
      <>
        <div className="w-full">{formBody}</div>
        {duplicateDialog}
      </>
    );
  }

  return (
    <>
    <div className="mx-auto max-w-lg">
      <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-md shadow-gray-200/50">
        <div className="border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Customer details</h2>
              <p className="text-xs text-gray-500">Name, phone, and one or more invoices.</p>
            </div>
          </div>
        </div>
        {formBody}
      </div>
    </div>
    {duplicateDialog}
    </>
  );
}
